import PropTypes from 'prop-types';
import {createUseStyles, useTheme} from 'react-jss';
import {connect} from 'react-redux';
import MessageType from '../../../../enums/MessageType';
import isUsingArtemisStyles from '../../../selectors/isUsingArtemisStyles';
import Spacing from '../../../style-helper/Spacing';
import MessageIcon from './MessageIcon/MessageIcon';

const useStyles = createUseStyles({
  message: (props) => {
    const messageColour = props.theme.core.messageColours[props.type];
    return {
      background: (props.isDeliveryTypeGiftCard) ? '#E6F0F8' : (props.isUsingArtemisStyles && props.theme.colors?.[props.type]?.light)
        ? props.theme.colors[props.type].light
        : messageColour.background,
      color: (props.isUsingArtemisStyles && props.theme.colors?.[props.type]?.base)
        ? props.theme.colors[props.type].base
        : messageColour.foreground,
      padding: props.inline ? Spacing.single : Spacing.double,
      marginTop: props.inline && Spacing.single,
      borderRadius: 4,
      display: 'flex',
      alignItems: 'center',
    };
  },
  icon: {
    alignSelf: 'flex-start',
    marginRight: Spacing.double,
  },
  messageDesc: {
    flex: 1,
    
    // This is carrying over from the previous implementation. Please fixme
    '& a': {
      color: 'inherit',
    },
  },
  primaryMessage: {
    fontWeight: props => (props.isDeliveryTypeGiftCard ? 'normal' : props.theme.core.boldFontWeight),
  },
  secondaryMessage: {
    marginTop: Spacing.half,
  },
});

/**
 * # Message box component
 *
 * Applies styles and provides built-in icons
 *
 * ## Example
 * Message content is passed by its children
 *
 * <Message type={MessageType.ERROR}>
 *   There are errors on the page
 * </Message>
 *
 * ## Options
 *
 * @param {string} props.type - One of the three MessageTypes: INFO, SUCCESS, ERROR (accessed using properties of the MessageType object), default: INFO
 * @param {Boolean} props.hideIcon - Whether to hide the icon from the message, default: false
 *
 * For more examples, see the Front End Documentation on Confluence
 */
export function Message(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  
  return (
    <div
      className={classes.message}
      data-test="Message"
      aria-describedby={props.associatedFieldId || null}
    >
      {!props.hideIcon && (
        <div className={classes.icon}>
          <MessageIcon type={props.type} />
        </div>
      )}
      <div className={classes.messageDesc} role={props.type === MessageType.ERROR ? 'alert' : undefined}>
        <div className={classes.primaryMessage}>
          {props.children}
        </div>
        {props.secondaryMessage && (
          <div className={classes.secondaryMessage}>
            {props.secondaryMessage}
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  isUsingArtemisStyles: isUsingArtemisStyles(state),
});

Message.propTypes = {
  associatedFieldId: PropTypes.string,
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(Object.values(MessageType)),
  hideIcon: PropTypes.bool,
  inline: PropTypes.bool,
  secondaryMessage: PropTypes.node,
  isUsingArtemisStyles: PropTypes.bool,
  isDeliveryTypeGiftCard: PropTypes.bool,
};

Message.defaultProps = {
  type: MessageType.INFO,
  hideIcon: false,
  secondaryMessage: null,
  inline: false,
};

export default connect(mapStateToProps)(Message);
